import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useLayoutEffect
} from 'react';
import styled, { css, keyframes } from 'styled-components';

// --- Types ---

export interface SelectOption {
  value: string | number;
  label: string;
  // Optional header the option is listed under (MultiSelectDropdown only);
  // consecutive options sharing a group are rendered under one
  // non-selectable header.
  group?: string;
}

interface PanelPosition {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
}

const PANEL_GAP = 4;
const PANEL_MARGIN = 8;
const PANEL_MIN_HEIGHT = 160;

/**
 * Positions the dropdown panel against the viewport (rendered in a portal
 * with a high z-index) so it is never clipped by a scrollable ancestor such
 * as the filter popup body. Flips above the trigger when there is more room.
 */
const usePanelPosition = (
  open: boolean,
  triggerRef: React.RefObject<HTMLElement>
): PanelPosition | null => {
  const [position, setPosition] = useState<PanelPosition | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    const update = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom - PANEL_MARGIN;
      const above = rect.top - PANEL_MARGIN;
      const openUp = below < PANEL_MIN_HEIGHT && above > below;
      const available = Math.max(openUp ? above : below, PANEL_MIN_HEIGHT);
      setPosition({
        top: openUp
          ? Math.max(PANEL_MARGIN, rect.top - available - PANEL_GAP)
          : rect.bottom + PANEL_GAP,
        left: rect.left,
        width: rect.width,
        maxHeight: available
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, triggerRef]);

  return position;
};

// --- Single Select ---

interface SelectDropdownProps {
  options: SelectOption[];
  value: string | number;
  onChange: (val: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export const SelectDropdown: React.FC<SelectDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  disabled = false
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const position = usePanelPosition(open, ref);

  const selectedLabel = options.find((o) => String(o.value) === String(value))
    ?.label;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        ref.current &&
        !ref.current.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  return (
    <Wrap ref={ref}>
      <Trigger
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        $disabled={disabled}
        $open={open}
        $hasValue={!!selectedLabel}
      >
        <TriggerText $faint={!selectedLabel}>
          {selectedLabel || placeholder}
        </TriggerText>
        <ChevronSvg $open={open}>
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
            <path
              d="M1 1l4 4 4-4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </ChevronSvg>
      </Trigger>
      {open && position && (
        <DropdownPanel ref={panelRef} style={position}>
          <OptionsList $maxHeight={position.maxHeight}>
            {options.map((opt) => (
              <OptionItem
                key={opt.value}
                $selected={String(opt.value) === String(value)}
                onClick={() => {
                  onChange(String(opt.value));
                  setOpen(false);
                }}
              >
                <OptionLabel>{opt.label}</OptionLabel>
                {String(opt.value) === String(value) && (
                  <CheckSvg>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M4 8.5l3 3L12 4"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </CheckSvg>
                )}
              </OptionItem>
            ))}
          </OptionsList>
        </DropdownPanel>
      )}
    </Wrap>
  );
};

// --- Multi Select ---

interface MultiSelectDropdownProps {
  options: SelectOption[];
  selected: SelectOption[];
  onChange: (val: SelectOption[]) => void;
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
}

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selected,
  onChange,
  placeholder = 'All',
  disabled = false,
  searchable = false
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const position = usePanelPosition(open, ref);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        ref.current &&
        !ref.current.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const normalizedQuery = query.trim().toLowerCase();
  const visibleOptions = normalizedQuery
    ? options.filter((o) => o.label.toLowerCase().includes(normalizedQuery))
    : options;

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  const toggleOption = useCallback(
    (opt: SelectOption) => {
      const exists = selected.find(
        (s) => String(s.value) === String(opt.value)
      );
      if (exists) {
        onChange(selected.filter((s) => String(s.value) !== String(opt.value)));
      } else {
        onChange([...selected, opt]);
      }
    },
    [selected, onChange]
  );

  const removeChip = useCallback(
    (val: string | number) => {
      onChange(selected.filter((s) => s.value !== val));
    },
    [selected, onChange]
  );

  return (
    <Wrap ref={ref}>
      <Trigger
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        $disabled={disabled}
        $open={open}
        $hasValue={selected.length > 0}
      >
        <TriggerContent>
          {selected.length > 0 ? (
            <ChipRow>
              {selected.map((s) => (
                <Chip key={s.value}>
                  <ChipText>{s.label}</ChipText>
                  <ChipRemove
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeChip(s.value);
                    }}
                  >
                    ×
                  </ChipRemove>
                </Chip>
              ))}
            </ChipRow>
          ) : (
            <TriggerText $faint>{placeholder}</TriggerText>
          )}
        </TriggerContent>
        <ChevronSvg $open={open}>
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
            <path
              d="M1 1l4 4 4-4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </ChevronSvg>
      </Trigger>
      {open && position && (
        <DropdownPanel ref={panelRef} style={position}>
          {searchable && (
            <SearchInput
              autoFocus
              type="text"
              value={query}
              placeholder="Search..."
              aria-label="Search options"
              onChange={(e) => setQuery(e.target.value)}
            />
          )}
          <OptionsList $maxHeight={position.maxHeight}>
            {visibleOptions.length === 0 && <NoResults>No results</NoResults>}
            {visibleOptions.map((opt, index) => {
              const showHeader =
                !!opt.group && opt.group !== visibleOptions[index - 1]?.group;
              const isSelected = !!selected.find(
                (s) => String(s.value) === String(opt.value)
              );
              return (
                <React.Fragment key={opt.value}>
                  {showHeader && <GroupHeader>{opt.group}</GroupHeader>}
                  <OptionItem
                    $selected={isSelected}
                    $indented={!!opt.group}
                    onClick={() => toggleOption(opt)}
                  >
                    <CheckBox $checked={isSelected}>
                      {isSelected && (
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M4 8.5l3 3L12 4"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </CheckBox>
                    <OptionLabel>{opt.label}</OptionLabel>
                  </OptionItem>
                </React.Fragment>
              );
            })}
          </OptionsList>
        </DropdownPanel>
      )}
    </Wrap>
  );
};

// --- Styled Components ---

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 8px 12px;
  border: none;
  border-bottom: 1px solid var(--line);
  background: transparent;
  font-size: 13px;
  color: var(--ink);
  outline: none;
`;

const NoResults = styled.div`
  padding: 8px 12px;
  font-size: 13px;
  color: var(--ink-faint, #999);
`;

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Wrap = styled.div`
  position: relative;
  width: 100%;
`;

const Trigger = styled.button<{
  $disabled: boolean;
  $open: boolean;
  $hasValue: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 100%;
  min-height: 32px;
  padding: 4px 8px 4px 0;
  border: none;
  border-bottom: 1.5px solid
    ${({ $open }) => ($open ? 'var(--terracotta)' : 'var(--line)')};
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  color: var(--ink);
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  transition: border-color 0.15s;
  text-align: left;

  &:hover {
    border-color: ${({ $disabled, $open }) =>
      $disabled
        ? 'var(--line)'
        : $open
          ? 'var(--terracotta)'
          : 'var(--ink-faint)'};
  }

  &:focus {
    outline: none;
    border-color: var(--terracotta);
  }
`;

const TriggerContent = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
`;

const TriggerText = styled.span<{ $faint?: boolean }>`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ $faint }) => ($faint ? 'var(--ink-faint)' : 'var(--ink)')};
`;

const ChevronSvg = styled.span<{ $open: boolean }>`
  display: flex;
  align-items: center;
  color: var(--ink-faint);
  transition: transform 0.2s;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0)')};
  flex-shrink: 0;
  margin-left: 6px;
`;

const GroupHeader = styled.div`
  padding: 8px 10px 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-faint);
  cursor: default;
  user-select: none;
`;

const DropdownPanel = styled.div`
  position: fixed;
  min-width: 180px;
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  box-shadow:
    0 8px 32px rgba(43, 61, 84, 0.1),
    0 2px 6px rgba(43, 61, 84, 0.06);
  z-index: 10000;
  animation: ${slideDown} 0.12s ease-out;
  overflow: hidden;
`;

const OptionsList = styled.div<{ $maxHeight: number }>`
  max-height: ${({ $maxHeight }) => Math.min(280, $maxHeight)}px;
  overflow-y: auto;
  padding: 4px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--line);
    border-radius: 3px;
  }
`;

const OptionItem = styled.div<{ $selected: boolean; $indented?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px 7px ${({ $indented }) => ($indented ? '18px' : '10px')};
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  color: var(--ink);
  transition: background 0.1s;
  background: ${({ $selected }) =>
    $selected ? 'var(--terracotta-soft)' : 'transparent'};

  &:hover {
    background: ${({ $selected }) =>
      $selected ? 'var(--terracotta-soft)' : 'var(--paper)'};
  }
`;

const OptionLabel = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CheckSvg = styled.span`
  display: flex;
  align-items: center;
  color: var(--terracotta);
  flex-shrink: 0;
`;

const CheckBox = styled.span<{ $checked: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  flex-shrink: 0;
  border: 1.5px solid
    ${({ $checked }) => ($checked ? 'var(--terracotta)' : 'var(--line)')};
  background: ${({ $checked }) =>
    $checked ? 'var(--terracotta)' : 'transparent'};
  color: var(--white);
  transition: all 0.12s;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 100%;
  max-height: 60px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 2px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--line);
    border-radius: 3px;
  }
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 11px;
  font-weight: 500;
  background: var(--terracotta-soft);
  color: var(--terracotta);
  border-radius: 4px;
  padding: 2px 5px 2px 7px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ChipText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChipRemove = styled.button`
  border: none;
  background: none;
  padding: 0 2px;
  font-size: 14px;
  line-height: 1;
  color: var(--terracotta);
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.1s;

  &:hover {
    opacity: 1;
  }
`;
